// ============================================================================
// Procedure Note 的 (位置) 佔位符解析
//
// DB 裡 procedureNote 的樣板（server/models/Examination.js 的 column default）
// 是一句英文，中間留一個**中文**佔位符給插入位置：
//
//   The endoscope was successfully inserted into (位置), and careful inspection
//   demonstrated normal colonic mucosa throughout the visualized colon. ...
//
// ── 為什麼要有這一層 ────────────────────────────────────────────────────────
//
// 原本的做法是「有 insertionLevel 就把佔位符換掉，沒有就原樣輸出」。但實測
// 2026-08-20 之後的 181 場檢查裡有 141 場（78%）根本沒錄到 insertionLevel，
// 於是 `(位置)` 這三個中文字直接印進**英文正式報告**交到病人手上。
//
// 使用者裁決的修法：沒錄到位置時，把「介系詞 + 佔位符」整段拿掉，讓句子收斂成
//
//   The endoscope was successfully inserted, and careful inspection ...
//
// 不臆測深度、不填 `the colon` 這種沒有臨床意義的字——報告寧可少講一句，
// 也不能講一句沒發生過的事。
//
// ── 為什麼移除策略不只認 `into` 一種寫法 ────────────────────────────────────
//
// procedureNote 是**可編輯欄位**（server/routes/report.js 有 PUT endpoint，
// 1.0 的 FindingsFormDetails 也有 @change），醫師可以把整段改掉。目前 DB 裡
// 只有一種樣板，但這支不能建立在「樣板永遠長這樣」的假設上，所以分三層：
//
//   1. 先移除「介系詞（片語）+ 佔位符」——這是唯一能讓句子讀起來仍然正常的解
//   2. 再移除任何殘留的裸佔位符——**保證輸出絕不可能含有佔位符**，不論樣板長怎樣
//   3. 最後收拾移除後留下的空白 / 標點（連續空白、逗號前的空白）——**只有真的移除了
//      東西才做**。note 沒有佔位符時整段原樣回傳，不去正規化醫師自己打的排版。
//
// 第 2 層是總保險：第 1 層漏接（醫師寫成別的句型）時，寧可讓句子讀起來突兀，
// 也不讓中文佔位符外洩。
//
// ⚠️ 這支是**顯示用**的純函式，不碰 i18n、不碰 Vue——note 為空（或整段只有佔位符）
//    時要顯示什麼，由呼叫端把字串當 fallbackText 傳進來。這樣「一段 note 最後
//    長什麼樣」整條決策都在這裡，元件只剩轉接，才測得到（SummaryEnReport.vue 的
//    template 用了 optional chaining，vue-template-es2015-compiler 解析不了，
//    整支元件在 jest 裡 import 不進來）。
// ============================================================================

/**
 * 佔位符本體。半形括號是 DB 樣板的寫法；全形括號是醫師用中文輸入法手動編輯
 * 時很容易打出來的變體，一併認掉，免得換一種括號就繞過整套移除邏輯。
 */
const PLACEHOLDER_SOURCE = '[(（]\\s*位置\\s*[)）]'

/** 裸佔位符（連同前面的空白一起吃掉，避免移除後留下兩個空白） */
const BARE_PLACEHOLDER_PATTERN = new RegExp(`\\s*${PLACEHOLDER_SOURCE}`, 'g')

/** 填值用（帶 g，一段裡有幾個就填幾個，不能只換第一個） */
const FILL_PLACEHOLDER_PATTERN = new RegExp(PLACEHOLDER_SOURCE, 'g')

/** 純比對用（不帶 g，避免 lastIndex 殘留），判斷「還有沒有佔位符」 */
const PLACEHOLDER_TEST_PATTERN = new RegExp(PLACEHOLDER_SOURCE)

/**
 * 佔位符前面的介系詞（片語）。長的排前面，否則 `into` 會先被 `to` 咬掉半截。
 * 前面的 `\s*` 連同空白一起吃掉，這樣 `inserted into (位置),` 移除後直接變成
 * `inserted,`——不會留下 `inserted ,` 這種逗號前多一格的痕跡。
 */
const PREPOSITION_PLACEHOLDER_PATTERN = new RegExp(
  `\\s*\\b(?:up\\s+to|as\\s+far\\s+as|into|onto|through|beyond|to|at|in)\\s+${PLACEHOLDER_SOURCE}`,
  'gi'
)

/**
 * 移除佔位符後的收拾工作。只處理「移除才會產生」的痕跡，不做整段正規化：
 *   - 連續的空白 / tab 併成一個（不碰換行，醫師分段的斷行要留著）
 *   - 標點前面的空白拿掉
 *   - 開頭的空白拿掉（佔位符出現在句首時會留下）
 *
 * ⚠️ 只有**真的移除了東西**才准呼叫（見 resolveProcedureNoteText 的 stripped 比對）。
 *    procedureNote 是醫師可編輯欄位，沒有佔位符可移除時我們沒有任何理由去動他的排版——
 *    無條件跑這支會把醫師自己打的雙空白、標點前空白靜默改寫掉。
 *
 * @param {string} text
 * @returns {string}
 */
function tidyAfterRemoval (text) {
  return text
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/^[ \t]+/, '')
}

/**
 * 把 insertionLevel 代碼換成對照表裡的顯示文字。
 * 查不到（代碼是舊的、對照表還沒載入、代碼根本不存在）時回傳空字串，
 * 讓呼叫端走「沒有位置」那條路——**不得**把整段文字塞回自己（1.0 的 bug）。
 *
 * @param {string|number|null|undefined} insertionLevel
 * @param {Array<{value: string, text: string}>} insertionLevelList
 * @returns {string}
 */
function resolveInsertionText (insertionLevel, insertionLevelList) {
  if (!insertionLevel) return ''
  if (!Array.isArray(insertionLevelList)) return ''

  const matched = insertionLevelList.find((item) => item && item.value === insertionLevel)
  return matched && matched.text ? matched.text : ''
}

/**
 * 產生要顯示 / 列印的 procedure note 文字。
 *
 * 有對得到的插入位置 → 佔位符換成位置名稱；
 * 沒有（未錄、代碼查不到）→ 連同介系詞一起移除，且保證輸出不含佔位符。
 *
 * @param {string|null|undefined} procedureNote 原始 note（通常是 DB 樣板）
 * @param {string|number|null|undefined} insertionLevel 插入位置代碼
 * @param {Array<{value: string, text: string}>} insertionLevelList 代碼對照表
 * @param {string} [fallbackText] note 為空 / 移除後什麼都不剩時要顯示的字（預設空字串）
 * @returns {string}
 */
export function resolveProcedureNoteText (procedureNote, insertionLevel, insertionLevelList, fallbackText = '') {
  if (!procedureNote || typeof procedureNote !== 'string') return fallbackText

  const insertionText = resolveInsertionText(insertionLevel, insertionLevelList)
  if (insertionText) {
    const filled = procedureNote.replace(FILL_PLACEHOLDER_PATTERN, insertionText)
    // 位置名稱本身若含佔位符（對照表被污染）就不算填成功，退回移除路徑，
    // 「輸出絕不含佔位符」這條保證不能有例外。
    if (!PLACEHOLDER_TEST_PATTERN.test(filled)) return filled
  }

  const stripped = procedureNote
    .replace(PREPOSITION_PLACEHOLDER_PATTERN, '')
    .replace(BARE_PLACEHOLDER_PATTERN, '')

  // 什麼都沒移除（這段 note 根本沒有佔位符）就原樣回傳——不得順手正規化醫師的排版
  const removed = stripped === procedureNote ? procedureNote : tidyAfterRemoval(stripped)
  return removed || fallbackText
}

export default resolveProcedureNoteText
